import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Store } from '@ngrx/store';
import { first } from 'rxjs';
import * as ort from 'onnxruntime-web';
import { openDB, IDBPDatabase } from 'idb';
import { env } from '../../../environments/env';
import { State } from '@completion/reducers';
import { getCurrentProject } from '@completion/selectors';

export interface PlateDetection {
  bbox: { x0: number; y0: number; x1: number; y1: number };
  confidence: number;
}

// ── IndexedDB schema for model caching ──────────────────────────────────────
const MODEL_DB_NAME = 'tag-plate-model';
const MODEL_DB_VERSION = 1;
const MODEL_STORE = 'models';
const MODEL_KEY = 'tag-plate-yolov8n';

interface CachedModel {
  key: string;
  version: string;
  data: ArrayBuffer;
  cachedAt: number;
}

/**
 * Client-side tag plate detector using a YOLOv8-nano ONNX model.
 *
 * Model lifecycle:
 *   1. On loadModel(), resolve the current projectId from the NgRx store
 *   2. Check IndexedDB for a cached model
 *   3. Ask the API: GET /api/projects/{id}/tag/model/meta  → { version }
 *   4. If cache is fresh → use cache (no download)
 *   5. If version changed or no cache → GET /api/projects/{id}/tag/model/download
 *   6. Store the binary in IndexedDB for next time
 *   7. If API is unreachable → use cached model if available
 *   8. Nothing at all → graceful fallback to manual-draw mode
 *
 * All HTTP calls go through Angular's HttpClient so the MSAL auth
 * interceptor attaches the Bearer token automatically.
 */
@Injectable({ providedIn: 'root' })
export class TagPlateDetectorService {
  private session: ort.InferenceSession | null = null;
  private loading = false;
  private loadAttempted = false;
  private dbPromise: Promise<IDBPDatabase> | null = null;

  /** Version string of the currently loaded model (shown in the UI badge). */
  modelVersion = '';

  /** Resolved once per service instance from the NgRx store. */
  private projectId: number | null = null;

  private readonly MODEL_SIZE = 640;
  private readonly CONF_THRESHOLD = 0.25;
  private readonly NMS_IOU = 0.45;

  get isModelAvailable(): boolean {
    return this.session !== null;
  }
  get isLoading(): boolean {
    return this.loading;
  }

  constructor(
    private readonly http: HttpClient,
    private readonly store: Store<State>,
  ) {}

  // ═══════════════════════════════════════════════════════════════════════════
  // IndexedDB CACHE
  // ═══════════════════════════════════════════════════════════════════════════

  private getDb(): Promise<IDBPDatabase> {
    if (!this.dbPromise) {
      this.dbPromise = openDB(MODEL_DB_NAME, MODEL_DB_VERSION, {
        upgrade(db) {
          if (!db.objectStoreNames.contains(MODEL_STORE)) {
            db.createObjectStore(MODEL_STORE, { keyPath: 'key' });
          }
        },
      });
    }
    return this.dbPromise;
  }

  private async getCachedModel(): Promise<CachedModel | undefined> {
    const db = await this.getDb();
    return db.get(MODEL_STORE, MODEL_KEY);
  }

  private async cacheModel(version: string, data: ArrayBuffer): Promise<void> {
    const db = await this.getDb();
    await db.put(MODEL_STORE, { key: MODEL_KEY, version, data, cachedAt: Date.now() } as CachedModel);
    console.log(`[TagPlateDetector] Cached model v${version} (${(data.byteLength / 1024 / 1024).toFixed(1)} MB)`);
  }

  /** Remove cached model from IndexedDB (e.g. after a corrupted download). */
  private async clearCache(): Promise<void> {
    const db = await this.getDb();
    await db.delete(MODEL_STORE, MODEL_KEY);
    console.log('[TagPlateDetector] Cleared cached model');
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // MODEL LOADING
  // ═══════════════════════════════════════════════════════════════════════════

  async loadModel(): Promise<boolean> {
    if (this.session) return true;
    if (this.loadAttempted) return false;
    this.loadAttempted = true;
    this.loading = true;

    try {
      // Resolve current project from NgRx store
      const project = await this.store.select(getCurrentProject).pipe(first()).toPromise();
      this.projectId = project?.id ?? null;
      if (!this.projectId) {
        console.warn('[TagPlateDetector] No current project – cannot fetch model');
        return false;
      }

      ort.env.wasm.numThreads = navigator.hardwareConcurrency ?? 4;
      ort.env.wasm.wasmPaths = '/assets/onnx/';

      const modelBytes = await this.resolveModelBytes();
      if (!modelBytes) {
        console.warn('[TagPlateDetector] No model available (API unreachable + no cache) – manual mode');
        return false;
      }

      this.session = await ort.InferenceSession.create(modelBytes, {
        executionProviders: ['wasm'],
        graphOptimizationLevel: 'all',
      });
      console.log(`[TagPlateDetector] Model v${this.modelVersion} ready`);
      return true;
    } catch (e) {
      console.warn('[TagPlateDetector] Model load failed – falling back to manual mode', e);
      this.session = null;
      // If the model bytes were corrupted (protobuf parse error), purge
      // the IndexedDB cache so the next attempt re-downloads a fresh copy.
      this.clearCache().catch(() => {});
      // Allow a fresh attempt next time (e.g. after a retake / re-open).
      this.loadAttempted = false;
      return false;
    } finally {
      this.loading = false;
    }
  }

  /**
   * Check meta → compare with cache → download only if needed.
   * Uses HttpClient so MSAL token is attached and the Angular dev-proxy is used.
   */
  private async resolveModelBytes(): Promise<ArrayBuffer | null> {
    const base = `${env.get().api.baseUrl}/projects/${this.projectId}/tag/model`;
    const cached = await this.getCachedModel().catch(() => undefined);

    // ── 1. Fetch version metadata ───────────────────────────────────────
    let serverVersion: string | null = null;
    try {
      const meta: any = await this.http.get(`${base}/meta`).toPromise();
      serverVersion = meta?.version ?? meta?.modelVersion ?? null;
      console.log(`[TagPlateDetector] Server model version: ${serverVersion ?? '(none)'}`);
    } catch {
      console.warn('[TagPlateDetector] Could not reach model/meta – checking cache');
    }

    // ── 2. Cache hit ────────────────────────────────────────────────────
    if (cached && serverVersion && cached.version === serverVersion) {
      console.log(`[TagPlateDetector] Cache hit – v${cached.version}`);
      this.modelVersion = cached.version;
      return cached.data;
    }

    // ── 3. Download if server has a version ────────────────────────────
    if (serverVersion) {
      try {
        console.log(`[TagPlateDetector] Downloading model v${serverVersion}…`);
        const data = await this.http.get(`${base}/download`, { responseType: 'arraybuffer' }).toPromise();

        if (data) {
          // Sanity check: ONNX files are protobuf and should be > 1 KB.
          // An HTML error page or empty response would fail session creation.
          if (data.byteLength < 1024) {
            console.warn(`[TagPlateDetector] Downloaded model is suspiciously small (${data.byteLength} bytes) – skipping`);
          } else {
            this.modelVersion = serverVersion;
            // Cache in the background – don't block inference startup
            this.cacheModel(serverVersion, data).catch((err) => console.warn('[TagPlateDetector] Failed to write cache', err));
            return data;
          }
        }
      } catch (e) {
        console.warn('[TagPlateDetector] Download failed', e);
      }
    }

    // ── 4. Offline fallback – stale cache ──────────────────────────────
    if (cached) {
      console.warn(`[TagPlateDetector] Using stale cache v${cached.version} (offline)`);
      this.modelVersion = cached.version;
      return cached.data;
    }

    return null;
  }

  /** Clear session and re-run the full load cycle (call after training). */
  async refreshModel(): Promise<boolean> {
    this.session?.release();
    this.session = null;
    this.loadAttempted = false;
    this.modelVersion = '';
    return this.loadModel();
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // INFERENCE
  // ═══════════════════════════════════════════════════════════════════════════

  async detect(canvas: HTMLCanvasElement): Promise<PlateDetection[]> {
    if (!this.session) return [];

    const origW = canvas.width;
    const origH = canvas.height;
    const { tensor, scale, padX, padY } = this.preprocess(canvas);

    const feeds: Record<string, ort.Tensor> = {};
    feeds[this.session.inputNames[0]] = tensor;

    const results = await this.session.run(feeds);
    const output = results[this.session.outputNames[0]];
    const rawData = output.data as Float32Array;
    const shape = output.dims;

    let detections: PlateDetection[];

    if (shape.length === 3 && shape[1] === 5) {
      detections = this.parseYolov8Output(rawData, shape[2], scale, padX, padY, origW, origH);
    } else if (shape.length === 3 && shape[2] === 5) {
      detections = this.parseYolov8Transposed(rawData, shape[1], scale, padX, padY, origW, origH);
    } else {
      console.warn('[TagPlateDetector] Unexpected output shape', shape);
      return [];
    }

    return this.nms(detections);
  }

  // ── Preprocessing ─────────────────────────────────────────────────────────

  private preprocess(canvas: HTMLCanvasElement): { tensor: ort.Tensor; scale: number; padX: number; padY: number } {
    const S = this.MODEL_SIZE;
    const scale = Math.min(S / canvas.width, S / canvas.height);
    const newW = Math.round(canvas.width * scale);
    const newH = Math.round(canvas.height * scale);
    const padX = Math.round((S - newW) / 2);
    const padY = Math.round((S - newH) / 2);

    const off = document.createElement('canvas');
    off.width = S;
    off.height = S;
    const ctx = off.getContext('2d')!;
    ctx.fillStyle = '#808080';
    ctx.fillRect(0, 0, S, S);
    ctx.drawImage(canvas, padX, padY, newW, newH);

    const imgData = ctx.getImageData(0, 0, S, S).data;
    const float32 = new Float32Array(3 * S * S);
    const C = S * S;
    for (let i = 0; i < C; i++) {
      float32[i] = imgData[i * 4] / 255; // R
      float32[C + i] = imgData[i * 4 + 1] / 255; // G
      float32[2 * C + i] = imgData[i * 4 + 2] / 255; // B
    }

    return { tensor: new ort.Tensor('float32', float32, [1, 3, S, S]), scale, padX, padY };
  }

  // ── Output parsers ────────────────────────────────────────────────────────

  private parseYolov8Output(
    data: Float32Array,
    N: number,
    scale: number,
    padX: number,
    padY: number,
    origW: number,
    origH: number,
  ): PlateDetection[] {
    const out: PlateDetection[] = [];
    for (let i = 0; i < N; i++) {
      const conf = data[4 * N + i];
      if (conf < this.CONF_THRESHOLD) continue;
      out.push(this.mapToOriginal(data[i], data[N + i], data[2 * N + i], data[3 * N + i], conf, scale, padX, padY, origW, origH));
    }
    return out;
  }

  private parseYolov8Transposed(
    data: Float32Array,
    N: number,
    scale: number,
    padX: number,
    padY: number,
    origW: number,
    origH: number,
  ): PlateDetection[] {
    const out: PlateDetection[] = [];
    for (let i = 0; i < N; i++) {
      const o = i * 5;
      const conf = data[o + 4];
      if (conf < this.CONF_THRESHOLD) continue;
      out.push(this.mapToOriginal(data[o], data[o + 1], data[o + 2], data[o + 3], conf, scale, padX, padY, origW, origH));
    }
    return out;
  }

  private mapToOriginal(
    cx: number,
    cy: number,
    w: number,
    h: number,
    conf: number,
    scale: number,
    padX: number,
    padY: number,
    origW: number,
    origH: number,
  ): PlateDetection {
    return {
      bbox: {
        x0: Math.max(0, Math.round((cx - w / 2 - padX) / scale)),
        y0: Math.max(0, Math.round((cy - h / 2 - padY) / scale)),
        x1: Math.min(origW, Math.round((cx + w / 2 - padX) / scale)),
        y1: Math.min(origH, Math.round((cy + h / 2 - padY) / scale)),
      },
      confidence: conf,
    };
  }

  // ── NMS ───────────────────────────────────────────────────────────────────

  private nms(detections: PlateDetection[]): PlateDetection[] {
    if (!detections.length) return [];
    detections.sort((a, b) => b.confidence - a.confidence);

    const keep: PlateDetection[] = [];
    const suppressed = new Set<number>();

    for (let i = 0; i < detections.length; i++) {
      if (suppressed.has(i)) continue;
      keep.push(detections[i]);
      for (let j = i + 1; j < detections.length; j++) {
        if (!suppressed.has(j) && this.iou(detections[i].bbox, detections[j].bbox) > this.NMS_IOU) {
          suppressed.add(j);
        }
      }
    }
    return keep;
  }

  private iou(a: { x0: number; y0: number; x1: number; y1: number }, b: { x0: number; y0: number; x1: number; y1: number }): number {
    const ix0 = Math.max(a.x0, b.x0),
      iy0 = Math.max(a.y0, b.y0);
    const ix1 = Math.min(a.x1, b.x1),
      iy1 = Math.min(a.y1, b.y1);
    const inter = Math.max(0, ix1 - ix0) * Math.max(0, iy1 - iy0);
    if (!inter) return 0;
    return inter / ((a.x1 - a.x0) * (a.y1 - a.y0) + (b.x1 - b.x0) * (b.y1 - b.y0) - inter);
  }
}
